import React, { forwardRef } from "react";
import { CpInput } from "@components";
import type { CpInputProps } from "../cp-input.component";
import { mergeRefs } from "@helpers";
import {
  useController,
  Control,
  RegisterOptions,
  UseControllerProps,
} from "react-hook-form";
import { CpInputFieldEmail } from "./cp-input-field-email.component";
import { CpInputFieldUrl } from "./cp-input-field-url.component";
import { CpInputFieldPhoneNumber } from "./cp-input-field-phone-number.component";

type CpInputFieldProps = Omit<
  CpInputProps,
  "value" | "onChange" | "onBlur" | "error"
> & {
  control: Control<any>;
  controllerOptions?: Omit<UseControllerProps, "name" | "control" | "rules">;
  fieldName: string;
  rules?: RegisterOptions;
  required?: boolean;
};

type CpInputFieldComponent = React.ForwardRefExoticComponent<
  CpInputFieldProps & React.RefAttributes<HTMLInputElement>
> & {
  Email: typeof CpInputFieldEmail;
  Url: typeof CpInputFieldUrl;
  PhoneNumber: typeof CpInputFieldPhoneNumber;
};

const CpInputFieldForwardRef = forwardRef<HTMLInputElement, CpInputFieldProps>(
  function CpInputField(props, ref) {
    const {
      fieldName,
      control,
      controllerOptions,
      required,
      rules = {},
      ...cpInputProps
    } = props;
    const mRules: RegisterOptions = required
      ? { required: "Field is required" }
      : {};
    const mergedRules = { ...mRules, ...rules };

    const {
      field: { value = "", onChange, onBlur, ref: fieldRef },
      fieldState: { error },
    } = useController({
      name: fieldName,
      control,
      rules: mergedRules,
      ...controllerOptions,
    });

    return (
      <CpInput
        value={value}
        onChange={onChange}
        onBlur={(e) => {
          const currentValue = e.target.value;
          if (currentValue) {
            onChange(currentValue.trim());
          }
          // @ts-expect-error onBlur types say () => void but it needs the event
          onBlur(e);
        }}
        error={error?.message}
        required={required}
        ref={mergeRefs(ref, fieldRef)}
        {...cpInputProps}
      />
    );
  },
);

export const CpInputField = Object.assign(CpInputFieldForwardRef, {
  Email: CpInputFieldEmail,
  Url: CpInputFieldUrl,
  PhoneNumber: CpInputFieldPhoneNumber,
}) as CpInputFieldComponent;
